import { config } from "../config/env";
import type { LinkedInFileType } from "../types/linkedin";
import { captureError } from "../utils/logger";
import type { paths } from "./types.gen";

export type ApiPaths = paths;

/** Progress for a chunked bulk-graph save. */
export interface SaveProgress {
	phase: "nodes" | "edges" | "events";
	completed: number;
	total: number;
	// Client ids persisted by the batch that just succeeded — the caller records
	// these so a retry after a partial failure resends only the unsaved remainder.
	savedIds?: string[];
}

function idsOf(items: Array<{ id?: string }>): string[] {
	return items
		.map((item) => item.id)
		.filter((id): id is string => typeof id === "string");
}

export type GraphSaveData = NonNullable<
	paths["/v1/graph/data"]["post"]["requestBody"]
>["content"]["application/json"];

type GraphSaveResponse =
	paths["/v1/graph/data"]["post"]["responses"]["200"]["content"]["application/json"];

// Gateway/LB statuses where the request almost certainly never reached (or
// committed in) the app — safe to retry. 500 is excluded: it may fire *after* a
// commit, and since the client owns the node UUIDs a blind retry could collide.
const RETRYABLE_STATUS = new Set([502, 503, 504]);
const RETRY_BACKOFF_MS = [500, 1500];

function chunk<T>(items: T[], size: number): T[][] {
	const out: T[][] = [];
	for (let i = 0; i < items.length; i += size) {
		out.push(items.slice(i, i + size));
	}
	return out;
}

function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Aborts when any input signal aborts (used to compose a timeout with a caller signal). */
function anySignal(signals: Array<AbortSignal | undefined>): AbortSignal {
	const controller = new AbortController();
	for (const signal of signals) {
		if (!signal) continue;
		if (signal.aborted) {
			controller.abort(signal.reason);
			break;
		}
		signal.addEventListener("abort", () => controller.abort(signal.reason), {
			once: true,
		});
	}
	return controller.signal;
}

/**
 * Simple OpenAPI client that uses the generated types
 */
export class ApiClient {
	private baseUrl: string;

	constructor(baseUrl: string = config.apiBaseUrl) {
		this.baseUrl = baseUrl;
	}

	private async request<T>(
		path: string,
		options: RequestInit & {
			/**
			 * Streams the response body and reports received bytes per chunk.
			 * totalBytes is Content-Length when present, else null. Note fetch
			 * yields decompressed bytes while Content-Length is the compressed
			 * size, so receivedBytes can exceed totalBytes — consumers must
			 * treat that as indeterminate.
			 */
			onDownloadProgress?: (
				receivedBytes: number,
				totalBytes: number | null,
			) => void;
		} = {},
	): Promise<T> {
		const url = `${this.baseUrl}${path}`;
		const {
			headers: optionHeaders,
			onDownloadProgress,
			...restOptions
		} = options;
		const response = await fetch(url, {
			credentials: "include",
			...restOptions,
			headers: {
				"Content-Type": "application/json",
				...(optionHeaders as Record<string, string>),
			},
		});

		if (!response.ok) {
			let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
			try {
				const errorBody = await response.text();
				errorMessage += ` - ${errorBody}`;
			} catch (_e) {
				// Could not parse error response
			}
			// Attach the HTTP status so callers can classify errors (and show safe,
			// user-facing messages) without parsing the raw body out of `.message`.
			const error = Object.assign(new Error(errorMessage), {
				status: response.status,
			});
			captureError(error, {
				operation: "apiRequest",
				metadata: {
					path,
					method: options.method || "GET",
					status: response.status,
				},
			});
			throw error;
		}

		if (onDownloadProgress && response.body) {
			const totalHeader = Number(response.headers.get("content-length"));
			const totalBytes =
				Number.isFinite(totalHeader) && totalHeader > 0 ? totalHeader : null;
			const reader = response.body.getReader();
			const chunks: Uint8Array[] = [];
			let receivedBytes = 0;
			for (;;) {
				const { done, value } = await reader.read();
				if (done) break;
				chunks.push(value);
				receivedBytes += value.byteLength;
				onDownloadProgress(receivedBytes, totalBytes);
			}
			const body = new Uint8Array(receivedBytes);
			let offset = 0;
			for (const chunk of chunks) {
				body.set(chunk, offset);
				offset += chunk.byteLength;
			}
			return JSON.parse(new TextDecoder().decode(body));
		}

		return response.json();
	}

	/**
	 * POST /v1/graph/data - Submit graph data
	 */
	async submitGraphData(
		data: NonNullable<
			paths["/v1/graph/data"]["post"]["requestBody"]
		>["content"]["application/json"],
	): Promise<
		paths["/v1/graph/data"]["post"]["responses"]["200"]["content"]["application/json"]
	> {
		return this.request("/v1/graph/data", {
			method: "POST",
			body: JSON.stringify(data),
		});
	}

	/**
	 * POST that survives large bulk imports: bounded per-request timeout,
	 * caller-abortable, and one retry on transient gateway errors. Unlike
	 * `request()` it does NOT report to Sentry — the caller classifies the
	 * failure once (see useGraphApi) to avoid double-reporting.
	 */
	private async resilientPost<T>(
		path: string,
		body: unknown,
		signal?: AbortSignal,
		options: { timeoutMs?: number } = {},
	): Promise<T> {
		const timeoutMs = options.timeoutMs ?? 120_000;
		const url = `${this.baseUrl}${path}`;
		let attempt = 0;

		while (true) {
			const timeoutController = new AbortController();
			const timer = setTimeout(
				() =>
					timeoutController.abort(new DOMException("Timeout", "TimeoutError")),
				timeoutMs,
			);
			try {
				const response = await fetch(url, {
					method: "POST",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(body),
					signal: anySignal([signal, timeoutController.signal]),
				});

				if (!response.ok) {
					if (
						RETRYABLE_STATUS.has(response.status) &&
						attempt < RETRY_BACKOFF_MS.length
					) {
						await delay(RETRY_BACKOFF_MS[attempt]);
						attempt++;
						continue;
					}
					let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
					try {
						const errorBody = await response.text();
						if (errorBody) errorMessage += ` - ${errorBody}`;
					} catch (_e) {
						// Could not read error body
					}
					throw Object.assign(new Error(errorMessage), {
						status: response.status,
					});
				}

				return (await response.json()) as T;
			} finally {
				clearTimeout(timer);
			}
		}
	}

	/**
	 * POST /v1/graph/data in resilient chunks. The client owns node UUIDs, so
	 * referential integrity only needs all nodes persisted before their edges:
	 * small payloads go as one atomic request (unchanged behavior); large ones
	 * send node-only batches first, then edge-only batches. Each request is its
	 * own transaction, so a failed chunk rolls back cleanly.
	 */
	async submitGraphDataChunked(
		data: GraphSaveData,
		options: {
			chunkSize?: number;
			onProgress?: (progress: SaveProgress) => void;
			signal?: AbortSignal;
		} = {},
	): Promise<GraphSaveResponse> {
		const chunkSize = options.chunkSize ?? 500;
		const { organizationId, nodes, edges, events = [] } = data;
		const { onProgress, signal } = options;

		// Common case: keep it a single atomic request.
		if (nodes.length + edges.length + events.length <= chunkSize) {
			const result = await this.resilientPost<GraphSaveResponse>(
				"/v1/graph/data",
				data,
				signal,
			);
			onProgress?.({
				phase: "edges",
				completed: edges.length,
				total: edges.length,
				savedIds: [...idsOf(nodes), ...idsOf(edges)],
			});
			return result;
		}

		const savedNodes: GraphSaveResponse["nodes"] = [];
		let doneNodes = 0;
		for (const batch of chunk(nodes, chunkSize)) {
			const result = await this.resilientPost<GraphSaveResponse>(
				"/v1/graph/data",
				{ organizationId, nodes: batch, edges: [] },
				signal,
			);
			savedNodes.push(...result.nodes);
			doneNodes += batch.length;
			onProgress?.({
				phase: "nodes",
				completed: doneNodes,
				total: nodes.length,
				savedIds: idsOf(batch),
			});
		}

		const savedEdges: GraphSaveResponse["edges"] = [];
		let doneEdges = 0;
		for (const batch of chunk(edges, chunkSize)) {
			const result = await this.resilientPost<GraphSaveResponse>(
				"/v1/graph/data",
				{ organizationId, nodes: [], edges: batch },
				signal,
			);
			savedEdges.push(...result.edges);
			doneEdges += batch.length;
			onProgress?.({
				phase: "edges",
				completed: doneEdges,
				total: edges.length,
				savedIds: idsOf(batch),
			});
		}

		// Events last: their participants may be nodes from any earlier batch.
		// They carry no client id, so they are not resumable — a partial failure
		// here leaves nodes and edges persisted and the events unrecorded.
		const savedEvents: NonNullable<GraphSaveResponse["events"]> = [];
		let doneEvents = 0;
		for (const batch of chunk(events, chunkSize)) {
			const result = await this.resilientPost<GraphSaveResponse>(
				"/v1/graph/data",
				{ organizationId, nodes: [], edges: [], events: batch },
				signal,
			);
			savedEvents.push(...(result.events ?? []));
			// Not a leftover: the api answers an events-only save with the edges
			// those events derived, and callers refresh from the returned ids.
			savedEdges.push(...result.edges);
			doneEvents += batch.length;
			onProgress?.({
				phase: "events",
				completed: doneEvents,
				total: events.length,
			});
		}

		return { nodes: savedNodes, edges: savedEdges, events: savedEvents };
	}

	/**
	 * POST /v1/graph/data/query - Query graph data
	 */
	async queryGraphData(
		data: NonNullable<
			paths["/v1/graph/data/query"]["post"]["requestBody"]
		>["content"]["application/json"],
		options?: {
			onDownloadProgress?: (
				receivedBytes: number,
				totalBytes: number | null,
			) => void;
		},
	): Promise<
		paths["/v1/graph/data/query"]["post"]["responses"]["200"]["content"]["application/json"]
	> {
		return this.request("/v1/graph/data/query", {
			method: "POST",
			body: JSON.stringify(data),
			onDownloadProgress: options?.onDownloadProgress,
		});
	}

	/**
	 * POST /v1/graph/data/search - Search graph data
	 */
	async searchGraphData(
		data: NonNullable<
			paths["/v1/graph/data/search"]["post"]["requestBody"]
		>["content"]["application/json"],
	): Promise<
		paths["/v1/graph/data/search"]["post"]["responses"]["200"]["content"]["application/json"]
	> {
		return this.request("/v1/graph/data/search", {
			method: "POST",
			body: JSON.stringify(data),
		});
	}

	/**
	 * PATCH /v1/graph/data - Bulk graph operations
	 */
	async bulkGraphOperations(
		data: NonNullable<
			paths["/v1/graph/data"]["patch"]["requestBody"]
		>["content"]["application/json"],
	): Promise<
		paths["/v1/graph/data"]["patch"]["responses"]["200"]["content"]["application/json"]
	> {
		return this.request("/v1/graph/data", {
			method: "PATCH",
			body: JSON.stringify(data),
		});
	}

	/**
	 * GET /v1/graph/parse/models - Get available parse models
	 *
	 * The model list is static for a session and multiple workflow hooks
	 * request it on mount, so concurrent and repeat calls share one promise.
	 * A rejected fetch clears the cache so the next call can retry.
	 */
	private parseModelsPromise: Promise<
		paths["/v1/graph/parse/models"]["get"]["responses"]["200"]["content"]["application/json"]
	> | null = null;

	async getParseModels(): Promise<
		paths["/v1/graph/parse/models"]["get"]["responses"]["200"]["content"]["application/json"]
	> {
		if (!this.parseModelsPromise) {
			this.parseModelsPromise = this.request<
				paths["/v1/graph/parse/models"]["get"]["responses"]["200"]["content"]["application/json"]
			>("/v1/graph/parse/models");
			this.parseModelsPromise.catch(() => {
				this.parseModelsPromise = null;
			});
		}
		return this.parseModelsPromise;
	}

	/**
	 * POST /v1/graph/parse - Parse text into nodes and edges
	 */
	async parseTextToGraph(
		data: NonNullable<
			paths["/v1/graph/parse"]["post"]["requestBody"]
		>["content"]["application/json"],
	): Promise<
		paths["/v1/graph/parse"]["post"]["responses"]["200"]["content"]["application/json"]
	> {
		return this.request("/v1/graph/parse", {
			method: "POST",
			body: JSON.stringify(data),
		});
	}

	/**
	 * POST /v1/graph/parse-image - Parse image into nodes and edges
	 */
	async parseImageToGraph(
		data: NonNullable<
			paths["/v1/graph/parse-image"]["post"]["requestBody"]
		>["content"]["application/json"],
	): Promise<
		paths["/v1/graph/parse-image"]["post"]["responses"]["200"]["content"]["application/json"]
	> {
		return this.request("/v1/graph/parse-image", {
			method: "POST",
			body: JSON.stringify(data),
		});
	}

	/**
	 * POST /v1/graph/path - Find optimal path between two nodes
	 */
	async findOptimalPath(
		data: NonNullable<
			paths["/v1/graph/path"]["post"]["requestBody"]
		>["content"]["application/json"],
	): Promise<
		paths["/v1/graph/path"]["post"]["responses"]["200"]["content"]["application/json"]
	> {
		return this.request("/v1/graph/path", {
			method: "POST",
			body: JSON.stringify(data),
		});
	}

	/**
	 * POST /v1/import/vcard - Import contacts from VCard file
	 */
	async importVCard(
		file: File,
		organizationId: string,
	): Promise<
		paths["/v1/import/vcard"]["post"]["responses"]["200"]["content"]["application/json"]
	> {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("organizationId", organizationId);

		const url = `${this.baseUrl}/v1/import/vcard`;
		const response = await fetch(url, {
			method: "POST",
			credentials: "include",
			body: formData,
		});

		if (!response.ok) {
			let errorMessage = `VCard import failed: ${response.status} ${response.statusText}`;
			try {
				const errorBody = await response.json();
				console.error("VCard Import Error:", errorBody);
				errorMessage = errorBody.message || errorMessage;
			} catch (_e) {
				console.error("Could not parse error response");
			}
			throw new Error(errorMessage);
		}

		return response.json();
	}

	/**
	 * POST /v1/import/csv - Import contacts from CSV file
	 */
	async importCSV(
		file: File,
		organizationId: string,
		nodeTypeName: string,
		options?: {
			importMode?: "generic" | "linkedin" | "apollo" | "auto";
			extractCompanies?: boolean;
		},
	): Promise<
		paths["/v1/import/csv"]["post"]["responses"]["200"]["content"]["application/json"]
	> {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("organizationId", organizationId);
		formData.append("nodeTypeName", nodeTypeName);

		if (options?.importMode) {
			formData.append("importMode", options.importMode);
		}
		if (options?.extractCompanies !== undefined) {
			formData.append("extractCompanies", String(options.extractCompanies));
		}

		const url = `${this.baseUrl}/v1/import/csv`;
		const response = await fetch(url, {
			method: "POST",
			credentials: "include",
			body: formData,
		});

		if (!response.ok) {
			let errorMessage = `CSV import failed: ${response.status} ${response.statusText}`;
			try {
				const errorBody = await response.json();
				console.error("CSV Import Error:", errorBody);
				errorMessage = errorBody.message || errorMessage;
			} catch (_e) {
				console.error("Could not parse error response");
			}
			throw new Error(errorMessage);
		}

		return response.json();
	}

	/**
	 * POST /v1/import/markdown - Import markdown files including Obsidian vault files
	 */
	async importMarkdown(
		files: File[],
		organizationId: string,
	): Promise<
		paths["/v1/import/markdown"]["post"]["responses"]["200"]["content"]["application/json"]
	> {
		const formData = new FormData();
		// Append each file to the form data
		for (const file of files) {
			formData.append("files", file);
		}
		formData.append("organizationId", organizationId);

		const url = `${this.baseUrl}/v1/import/markdown`;
		const response = await fetch(url, {
			method: "POST",
			credentials: "include",
			body: formData,
		});

		if (!response.ok) {
			let errorMessage = `Markdown import failed: ${response.status} ${response.statusText}`;
			try {
				const errorBody = await response.json();
				console.error("Markdown Import Error:", errorBody);
				errorMessage = errorBody.message || errorMessage;
			} catch (_e) {
				console.error("Could not parse error response");
			}
			throw new Error(errorMessage);
		}

		return response.json();
	}

	/**
	 * POST /v1/import/linkedin - Import multiple LinkedIn data files
	 */
	async importLinkedIn(
		files: Partial<Record<LinkedInFileType, File>>,
		organizationId: string,
		settings: Readonly<Record<string, boolean>>,
		signal?: AbortSignal,
	): Promise<
		paths["/v1/import/linkedin"]["post"]["responses"]["200"]["content"]["application/json"]
	> {
		const formData = new FormData();
		formData.append("organizationId", organizationId);
		for (const [name, enabled] of Object.entries(settings)) {
			formData.append(name, String(enabled));
		}
		for (const [name, file] of Object.entries(files)) {
			if (file) formData.append(name, file);
		}

		const url = `${this.baseUrl}/v1/import/linkedin`;
		const response = await fetch(url, {
			method: "POST",
			credentials: "include",
			body: formData,
			signal,
		});

		if (!response.ok) {
			let errorMessage = `LinkedIn import failed: ${response.status} ${response.statusText}`;
			try {
				const errorBody = await response.json();
				console.error("LinkedIn Import Error:", errorBody);
				errorMessage = errorBody.message || errorMessage;
			} catch (_e) {
				console.error("Could not parse error response");
			}
			throw new Error(errorMessage);
		}

		return response.json();
	}

	/**
	 * GET /v1/export/metadata - Get export metadata (distinct types and property keys)
	 */
	async getExportMetadata(organizationId: string): Promise<{
		nodeTypeNames: string[];
		edgeTypeNames: string[];
		nodePropertyKeys: string[];
		edgePropertyKeys: string[];
	}> {
		return this.request(
			`/v1/export/metadata?organizationId=${encodeURIComponent(organizationId)}`,
		);
	}

	/**
	 * POST /v1/export - Export organization data as JSON file
	 * Returns a Blob for download.
	 */
	async exportData(data: {
		organizationId: string;
		nodeIds: string[];
		edgeIds: string[];
		nodePropertyKeys?: string[];
		edgePropertyKeys?: string[];
	}): Promise<Blob> {
		const url = `${this.baseUrl}/v1/export`;
		const response = await fetch(url, {
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(data),
		});

		if (!response.ok) {
			let errorMessage = `Export failed: ${response.status} ${response.statusText}`;
			try {
				const errorBody = await response.json();
				errorMessage = errorBody.message || errorMessage;
			} catch (_e) {
				// Could not parse error response
			}
			throw new Error(errorMessage);
		}

		return response.blob();
	}

	/**
	 * POST /v1/import/json - Parse Relatance JSON export for re-import
	 */
	async importJson(
		file: File,
		organizationId: string,
	): Promise<{
		nodes: Array<{
			typeName: string | null;
			displayName: string;
			properties: Record<string, unknown>;
		}>;
		edges: Array<{
			typeName: string | null;
			sourceDisplayName: string;
			targetDisplayName: string;
			weight: number;
			properties: Record<string, unknown>;
		}>;
		summary: {
			totalNodes: number;
			totalEdges: number;
			nodeTypes: string[];
			edgeTypes: string[];
		};
	}> {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("organizationId", organizationId);

		const url = `${this.baseUrl}/v1/import/json`;
		const response = await fetch(url, {
			method: "POST",
			credentials: "include",
			body: formData,
		});

		if (!response.ok) {
			let errorMessage = `JSON import failed: ${response.status} ${response.statusText}`;
			try {
				const errorBody = await response.json();
				errorMessage = errorBody.message || errorMessage;
			} catch (_e) {
				// Could not parse error response
			}
			throw new Error(errorMessage);
		}

		return response.json();
	}
	async getNodeImageUploadUrl(
		nodeId: string,
		organizationId: string,
	): Promise<{ uploadUrl: string }> {
		return this.request(`/v1/nodes/${nodeId}/image/upload-url`, {
			method: "POST",
			headers: { "x-organization-id": organizationId },
		});
	}

	async confirmNodeImage(
		nodeId: string,
		organizationId: string,
	): Promise<{ success: boolean }> {
		return this.request(`/v1/nodes/${nodeId}/image/confirm`, {
			method: "POST",
			headers: { "x-organization-id": organizationId },
		});
	}

	async deleteNodeImage(
		nodeId: string,
		organizationId: string,
	): Promise<{ success: boolean }> {
		return this.request(`/v1/nodes/${nodeId}/image`, {
			method: "DELETE",
			headers: { "x-organization-id": organizationId },
		});
	}

	async getNodeImageUrls(
		organizationId: string,
		nodeIds: string[],
	): Promise<{ urls: Record<string, string> }> {
		return this.request("/v1/nodes/image-urls", {
			method: "POST",
			body: JSON.stringify({ organizationId, nodeIds }),
		});
	}
}

export const apiClient = new ApiClient();
